import { env } from "./env.js";
import { ApiError } from "./api-types.js";

/**
 * 401 处理钩子。main.tsx 启动时注入：清 auth + navigate(ADMIN_LOGIN)。
 * 测试可重设以验证行为。
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * 统一 fetch 包装。
 *
 * - GET/POST/PUT/PATCH/DELETE 同入口
 * - Authorization 通过 token 参数注入（caller 持 useAtomValue(authAtom)?.token）
 * - 200/2xx + body → parsed JSON
 * - 204 → undefined
 * - 401 → 调 onUnauthorized handler + throw ApiError("unauthorized")
 * - 其他非 2xx → throw ApiError，code 取响应 body.error 或 "unknown"
 *
 * **不**自动注入 Authorization；caller 必须显式传 token。
 * 这样测试可以独立验证 401 行为而不耦合 auth-store。
 */
export async function apiCall<T = unknown>(
  method: string,
  path: string,
  options: {
    body?: unknown;
    token?: string;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (options.token) {
    headers["Authorization"] = `Bearer ${options.token}`;
  }

  const url = `${env.API_BASE}${path}`;
  const res = await fetch(url, {
    method,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, "unauthorized", "登录已过期或凭据无效");
  }

  if (res.status === 204) {
    return undefined as T;
  }

  if (!res.ok) {
    let code = "unknown";
    let message = res.statusText || "请求失败";
    let details: unknown;
    try {
      const body = (await res.json()) as {
        error?: string | { message?: string };
        message?: string;
        violations?: unknown;
      };
      // 两种失败体形状都在产线上活着：
      // - 扁平（admin-portal 自己的路由）：{ error: string, message?: string }
      // - 嵌套信封（dosia-enterprise review-actions failWith 透传）：{ error: { message: string } }
      if (body.error && typeof body.error === "object" && typeof body.error.message === "string") {
        code = "upstream-error";
        message = body.error.message;
      } else {
        if (typeof body.error === "string") code = body.error;
        if (typeof body.message === "string") message = body.message;
      }
      // 结构化校验负载透传（如平台工具策展 PUT 的 400 { violations }）。
      if (Array.isArray(body.violations)) {
        details = body.violations;
      } else {
        // 其余错误体（如 N-83 飞书发布的 409 { by, name } / 422 { missing } / { reason }）
        // 整体透传，调用方按错误码自行取字段（不新增第二套 details 形状）。
        details = body;
      }
    } catch {
      // body 不是 JSON 也无所谓
    }
    throw new ApiError(res.status, code, message, details);
  }

  return (await res.json()) as T;
}
